import {
  getCostCenterTopUpsHistory,
  editCostCenterLinkedCards,
  getCostCenter,
} from '@api/cost-centers';
import {
  OpensearchSearchRequestOpenSearchDataTypeEnum,
  ResponseErrorDto,
} from '@interfaces/models';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useCreditCards } from '@pages/cost-centers/hooks';
import { getOpensearchTableConfig } from '@api/opensearch';
import { useCardsStore } from '@pages/cost-centers/store';
import { getCoreRowModel } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo } from 'react';
import { useReactTable } from '@tanstack/react-table';
import { useAutoColumns } from '@shared/components';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@shared/hooks';
import { toast } from '@ui/components';

export const useCostCenterAccountDetails = ({
  costCenterId,
  onSuccess,
}: {
  onSuccess?: () => void;
  costCenterId: string;
}) => {
  const { t } = useTranslation();
  const { cards, setCards, removeCard, clearCards } = useCardsStore();
  const { companyId } = useCurrentUser();

  const { data: costCenterData, isLoading: isCostCenterLoading } = useQuery({
    queryKey: ['cost-center', costCenterId],
    queryFn: () => getCostCenter(costCenterId),
  });

  const {
    cards: cardsData,
    cardsLoading,
    refetchCards,
  } = useCreditCards({
    accountId: costCenterData?.technicalAccountId,
  });

  const { data: tableConfig, isLoading: isTableConfigLoading } = useQuery({
    queryKey: ['cost-center-account-details'],
    queryFn: () =>
      getOpensearchTableConfig(OpensearchSearchRequestOpenSearchDataTypeEnum.ACCOUNT_TOP_UPS),
  });

  const { data: topUps, isLoading: isTopUpsLoading } = useQuery({
    queryKey: ['cost-center-top-ups'],
    queryFn: () =>
      getCostCenterTopUpsHistory(
        OpensearchSearchRequestOpenSearchDataTypeEnum.ACCOUNT_TOP_UPS,
        costCenterId,
      ),
  });

  const { mutate: editCards, isPending: isEditCardsPending } = useMutation({
    mutationFn: (paymentCards: string[]) => {
      if (!costCenterData?.technicalAccountId) {
        throw new Error('Cost center technical account ID is not found');
      }
      return editCostCenterLinkedCards(costCenterData?.technicalAccountId, { paymentCards });
    },
    onSuccess: () => {
      toast.success(t('COST_CENTER.ACCOUNT_DETAILS.CARD_EDIT_SUCCESS'));
      refetchCards();
      onSuccess?.();
    },
    onError: ({ error }: { error: ResponseErrorDto }) => {
      toast.error(t(`API_ERRORS.${error.errorCode}`));
    },
  });

  const { columnDefinitions } = useAutoColumns({
    columns: tableConfig ?? [],
  });

  const columnsLength = useMemo(() => {
    return columnDefinitions.length;
  }, [columnDefinitions]);

  const isLoading = useMemo(() => {
    return cardsLoading || isTableConfigLoading;
  }, [cardsLoading, isTableConfigLoading]);

  const tableData = useMemo(() => {
    return topUps?.data ?? [];
  }, [topUps]);

  const table = useReactTable({
    data: tableData,
    columns: columnDefinitions,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    setCards(cardsData ?? []);
    return () => {
      clearCards();
    };
  }, [cardsData, setCards, clearCards]);

  const resetCards = useCallback(() => {
    refetchCards();
    setCards(cardsData ?? []);
  }, [refetchCards, cardsData, setCards]);

  const editPaymentCards = useCallback(() => {
    editCards(cards.map(({ paymentMethodId }) => paymentMethodId));
  }, [cards, editCards]);

  return {
    isCostCenterLoading,
    isEditCardsPending,
    editPaymentCards,
    isTopUpsLoading,
    costCenterData,
    columnsLength,
    removeCard,
    resetCards,
    isLoading,
    companyId,
    table,
    cards,
    t,
  };
};
